import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  Link,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  AccountBalanceWallet as WalletIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const paiseToInr = (paise) => Number(paise || 0) / 100;

const inrToPaise = (inr) => Math.round(Number(inr || 0) * 100);

const formatInrLabel = (paise) => {
  const inr = paiseToInr(paise);
  if (!Number.isFinite(inr) || inr <= 0) return '₹0';
  return inr % 1 === 0 ? `₹${inr}` : `₹${inr.toFixed(2)}`;
};

const SPEND_GROUPS = [
  {
    id: 'grow',
    title: 'Connect with Loksy',
    hint: 'Advertise, ads, coupons, extra shop photos, shop orders, shop profile — set coin price per feature.',
  },
  {
    id: 'operate',
    title: 'My Business Desk',
    hint: 'Bills, catalog, print cards, leads, reports — set coin price per feature.',
  },
  {
    id: 'platform',
    title: 'Platform / other',
    hint: 'AI posters, LMS discount, billing blocks, and other engine rules.',
  },
];

function spendGroupId(rule) {
  if (rule.zone === 'grow' || rule.metadata?.zone === 'grow') return 'grow';
  if (rule.zone === 'operate' || rule.metadata?.zone === 'operate') return 'operate';
  return 'platform';
}

/**
 * Coin Management — Loksy Wallet economy (closed-loop service credits).
 */
const CoinManagement = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [envFlag, setEnvFlag] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    effectiveEnabled: false,
    legalBlurb: '',
    refundPolicyBlurb: '',
    expiryPolicyBlurb: '',
    gstDisplayBlurb: '',
    abuseAndReversalBlurb: '',
    consentCheckboxLabel: '',
    coinTermsVersion: '',
    coinTermsBody: '',
    engineLive: false,
  });
  const [analytics, setAnalytics] = useState(null);
  const [plans, setPlans] = useState([]);
  const [earnRules, setEarnRules] = useState([]);
  const [spendRules, setSpendRules] = useState([]);
  const [adjust, setAdjust] = useState({
    ownerType: 'MITRA',
    ownerId: '',
    amount: '',
    direction: 'credit',
    description: '',
    expiryDays: '90',
  });
  const [wallets, setWallets] = useState([]);
  const [walletsTotal, setWalletsTotal] = useState(0);
  const [walletSearch, setWalletSearch] = useState('');
  const [walletType, setWalletType] = useState('');
  const [walletsLoading, setWalletsLoading] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/coins/admin/overview`, {
        headers: authHeaders(),
      });
      if (res.data?.success) {
        const d = res.data.data || {};
        setEnvFlag(!!d.envFlag);
        const s = d.settings || {};
        setSettings({
          enabled: !!s.rawEnabled,
          effectiveEnabled: !!s.enabled,
          engineLive: !!s.engineLive,
          legalBlurb: s.legalBlurb || '',
          refundPolicyBlurb: s.refundPolicyBlurb || '',
          expiryPolicyBlurb: s.expiryPolicyBlurb || '',
          gstDisplayBlurb: s.gstDisplayBlurb || '',
          abuseAndReversalBlurb: s.abuseAndReversalBlurb || '',
          consentCheckboxLabel: s.consentCheckboxLabel || '',
          coinTermsVersion: s.coinTermsVersion || '',
          coinTermsBody: s.coinTermsBody || '',
        });
        setAnalytics(d.analytics || null);
        setPlans(d.plans || []);
        setEarnRules(d.earnRules || []);
        setSpendRules(d.spendRules || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load Coin Management');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWallets = useCallback(async () => {
    setWalletsLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/coins/admin/wallets`, {
        headers: authHeaders(),
        params: {
          search: walletSearch || undefined,
          ownerType: walletType || undefined,
          limit: 100,
        },
      });
      if (res.data?.success) {
        setWallets(res.data.data?.wallets || []);
        setWalletsTotal(res.data.data?.total || 0);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load wallets');
    } finally {
      setWalletsLoading(false);
    }
  }, [walletSearch, walletType]);

  useEffect(() => {
    document.title = 'Coin Management - LOCSY';
    load();
  }, [load]);

  useEffect(() => {
    if (tab === 0) loadWallets();
  }, [tab, loadWallets]);

  const saveLegal = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/coins/admin/legal`,
        {
          enabled: settings.enabled,
          legalBlurb: settings.legalBlurb,
          refundPolicyBlurb: settings.refundPolicyBlurb,
          expiryPolicyBlurb: settings.expiryPolicyBlurb,
          gstDisplayBlurb: settings.gstDisplayBlurb,
          abuseAndReversalBlurb: settings.abuseAndReversalBlurb,
          consentCheckboxLabel: settings.consentCheckboxLabel,
          coinTermsVersion: settings.coinTermsVersion,
          coinTermsBody: settings.coinTermsBody,
        },
        { headers: authHeaders() },
      );
      toast.success('Legal settings saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const savePlan = async (plan) => {
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/coins/admin/plans`,
        {
          plan: {
            ...plan,
            pricePaise: Number(plan.pricePaise),
            baseCoins: Number(plan.baseCoins),
            bonusCoins: Number(plan.bonusCoins || 0),
            displayOrder: Number(plan.displayOrder || 0),
            bonusExpiryDays:
              plan.bonusExpiryDays === '' || plan.bonusExpiryDays == null
                ? null
                : Number(plan.bonusExpiryDays),
          },
        },
        { headers: authHeaders() },
      );
      toast.success('Plan saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Plan save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveEarnRule = async (rule) => {
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/coins/admin/earn-rules`,
        { rule },
        { headers: authHeaders() },
      );
      toast.success('Earn rule saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Earn rule save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveSpendRule = async (rule) => {
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/coins/admin/spend-rules`,
        { rule },
        { headers: authHeaders() },
      );
      toast.success('Spend rule saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Spend rule save failed');
    } finally {
      setSaving(false);
    }
  };

  const runAdjust = async () => {
    if (!adjust.ownerId || !adjust.amount) {
      toast.error('Owner ID and amount required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/coins/admin/adjust`,
        {
          ...adjust,
          amount: Number(adjust.amount),
          expiryDays: Number(adjust.expiryDays || 90),
        },
        { headers: authHeaders() },
      );
      toast.success('Adjustment applied');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Adjustment failed');
    } finally {
      setSaving(false);
    }
  };

  const updatePlanLocal = (planKey, patch) => {
    setPlans((prev) =>
      prev.map((p) => (p.planKey === planKey ? { ...p, ...patch } : p)),
    );
  };

  const updateEarnLocal = (ruleKey, patch) => {
    setEarnRules((prev) =>
      prev.map((r) => (r.ruleKey === ruleKey ? { ...r, ...patch } : r)),
    );
  };

  const updateSpendLocal = (ruleKey, patch) => {
    setSpendRules((prev) =>
      prev.map((r) => (r.ruleKey === ruleKey ? { ...r, ...patch } : r)),
    );
  };

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <WalletIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Wallet & Coin Management
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Loksy Wallet — closed-loop service credits. Not cash. Non-transferable.
          Non-withdrawable. Search wallets, recharge plans, earn/spend rules, and legal text.
        </Typography>

        {!envFlag && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Wallet recharge</strong> works when the Overview toggle is ON.
            Set server env <code>COIN_ENGINE_ENABLED=true</code> only when you want
            automatic coin debit/credit on billing, coupons, and other feature hooks.
          </Alert>
        )}

        {loading ? (
          <Box py={6} textAlign="center">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper sx={{ px: 2 }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Wallets" />
                <Tab label="Overview" />
                <Tab label="Recharge Plans" />
                <Tab label="Earn Rules" />
                <Tab label="Spend Rules" />
                <Tab label="Legal" />
                <Tab label="Adjustments" />
              </Tabs>
            </Paper>

            <TabPanel value={tab} index={0}>
              <Paper sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  mb={2}
                  alignItems={{ md: 'center' }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search shop name, mobile, address, or owner ID..."
                    value={walletSearch}
                    onChange={(e) => setWalletSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') loadWallets();
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Type"
                    value={walletType}
                    onChange={(e) => setWalletType(e.target.value)}
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="MITRA">MITRA</MenuItem>
                    <MenuItem value="CITIZEN">CITIZEN</MenuItem>
                    <MenuItem value="STUDENT">STUDENT</MenuItem>
                    <MenuItem value="MARKETING_PARTNER">MARKETING_PARTNER</MenuItem>
                  </TextField>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={loadWallets}
                    disabled={walletsLoading}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setTab(6)}
                  >
                    + Add Coins
                  </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {walletsTotal} wallet{walletsTotal === 1 ? '' : 's'}
                </Typography>
                {walletsLoading ? (
                  <Box py={4} textAlign="center">
                    <CircularProgress />
                  </Box>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Owner type</TableCell>
                        <TableCell>Shop / owner</TableCell>
                        <TableCell>Balance (coins)</TableCell>
                        <TableCell>Available</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Last updated</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {wallets.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7}>
                            No Loksy Wallet accounts yet. Wallets are created when a
                            Mitra, Citizen, or Student opens Wallet or receives coins.
                          </TableCell>
                        </TableRow>
                      ) : (
                        wallets.map((w) => (
                          <TableRow key={w.id} hover>
                            <TableCell>{w.ownerType}</TableCell>
                            <TableCell sx={{ maxWidth: 360 }}>
                              <Typography variant="body2" fontWeight={700}>
                                {w.displayName || '—'}
                              </Typography>
                              {w.address ? (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {w.address}
                                </Typography>
                              ) : null}
                              {w.phone ? (
                                <Stack direction="row" spacing={0.5} alignItems="center" mt={0.25}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    component="a"
                                    href={w.telHref || `tel:${w.phone}`}
                                    aria-label={`Call ${w.phone}`}
                                  >
                                    <PhoneIcon fontSize="inherit" />
                                  </IconButton>
                                  <Link
                                    href={w.telHref || `tel:${w.phone}`}
                                    underline="hover"
                                    variant="body2"
                                  >
                                    {w.phone}
                                  </Link>
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  No mobile on file
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <strong>{w.balance}</strong>
                            </TableCell>
                            <TableCell>{w.available}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={w.status}
                                color={w.status === 'active' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {w.updatedAt
                                ? new Date(w.updatedAt).toLocaleString()
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() => {
                                  setAdjust((a) => ({
                                    ...a,
                                    ownerType: w.ownerType,
                                    ownerId: w.ownerId,
                                  }));
                                  setTab(6);
                                }}
                              >
                                Adjust
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <Stack spacing={2}>
                <Paper sx={{ p: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!settings.enabled}
                        onChange={(e) =>
                          setSettings((s) => ({
                            ...s,
                            enabled: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={`Wallet & recharge enabled${
                      settings.effectiveEnabled ? ' — ON' : ' — OFF'
                    }`}
                  />
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1, mb: 1 }}>
                    <Chip
                      size="small"
                      color={settings.effectiveEnabled ? 'success' : 'default'}
                      label={
                        settings.effectiveEnabled
                          ? 'Mitra/Citizen recharge: ON'
                          : 'Mitra/Citizen recharge: OFF'
                      }
                    />
                    <Chip
                      size="small"
                      color={settings.engineLive ? 'success' : 'warning'}
                      label={
                        settings.engineLive
                          ? 'Feature spend hooks: LIVE'
                          : 'Feature spend hooks: set COIN_ENGINE_ENABLED'
                      }
                    />
                  </Stack>
                  <Button
                    sx={{ ml: 2 }}
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                    onClick={saveLegal}
                  >
                    Save enable flag
                  </Button>
                  <Button
                    sx={{ ml: 1 }}
                    startIcon={<RefreshIcon />}
                    onClick={load}
                  >
                    Refresh
                  </Button>
                </Paper>

                {analytics && (
                  <Paper sx={{ p: 2 }}>
                    <Typography fontWeight={700} mb={1}>
                      Analytics
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
                      <Chip label={`Purchased: ${analytics.totalPurchased}`} />
                      <Chip label={`Rewards: ${analytics.totalRewards}`} />
                      <Chip label={`Consumed: ${analytics.totalConsumed}`} />
                      <Chip
                        color="primary"
                        label={`Outstanding: ${analytics.totalOutstanding}`}
                      />
                    </Stack>
                    <Typography variant="subtitle2" gutterBottom>
                      Recharge plan usage
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Plan</TableCell>
                          <TableCell>Orders</TableCell>
                          <TableCell>Revenue (paise)</TableCell>
                          <TableCell>Coins</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(analytics.rechargePlanUsage || []).map((r) => (
                          <TableRow key={r.plan_key}>
                            <TableCell>{r.plan_key}</TableCell>
                            <TableCell>{r.orders}</TableCell>
                            <TableCell>{r.revenue_paise}</TableCell>
                            <TableCell>{r.coins}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                )}
              </Stack>
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Mitra shows the <strong>Price (₹)</strong> field, not Label alone. To change
                ₹200 → ₹2, set Price to <strong>2</strong> and click Save (200 paise, not 20000).
              </Alert>
              <Stack spacing={2}>
                {plans.map((p) => (
                  <Paper key={p.planKey} sx={{ p: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                      <TextField
                        label="Label"
                        size="small"
                        value={p.label || ''}
                        onChange={(e) =>
                          updatePlanLocal(p.planKey, { label: e.target.value })
                        }
                      />
                      <TextField
                        label="Price (₹)"
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText={`Stored as ${Number(p.pricePaise || 0)} paise · Mitra & Razorpay use this`}
                        value={paiseToInr(p.pricePaise)}
                        onChange={(e) => {
                          const paise = inrToPaise(e.target.value);
                          updatePlanLocal(p.planKey, {
                            pricePaise: paise,
                            label: formatInrLabel(paise),
                          });
                        }}
                      />
                      <TextField
                        label="Base coins"
                        size="small"
                        type="number"
                        value={p.baseCoins ?? ''}
                        onChange={(e) =>
                          updatePlanLocal(p.planKey, {
                            baseCoins: e.target.value,
                          })
                        }
                      />
                      <TextField
                        label="Bonus coins"
                        size="small"
                        type="number"
                        value={p.bonusCoins ?? ''}
                        onChange={(e) =>
                          updatePlanLocal(p.planKey, {
                            bonusCoins: e.target.value,
                          })
                        }
                      />
                      <TextField
                        label="Bonus expiry days"
                        size="small"
                        type="number"
                        value={p.bonusExpiryDays ?? ''}
                        onChange={(e) =>
                          updatePlanLocal(p.planKey, {
                            bonusExpiryDays: e.target.value,
                          })
                        }
                      />
                      <TextField
                        label="Order"
                        size="small"
                        type="number"
                        value={p.displayOrder ?? 0}
                        onChange={(e) =>
                          updatePlanLocal(p.planKey, {
                            displayOrder: e.target.value,
                          })
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!p.isActive}
                            onChange={(e) =>
                              updatePlanLocal(p.planKey, {
                                isActive: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Active"
                      />
                      <Button
                        variant="outlined"
                        disabled={saving}
                        onClick={() => savePlan(p)}
                      >
                        Save
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {p.planKey} · Charge ₹{paiseToInr(p.pricePaise)} · Total:{' '}
                      {Number(p.baseCoins || 0) + Number(p.bonusCoins || 0)} coins
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </TabPanel>

            <TabPanel value={tab} index={3}>
              <Stack spacing={2}>
                {earnRules.map((r) => (
                  <Paper key={r.ruleKey} sx={{ p: 2 }}>
                    <Typography fontWeight={600}>{r.label}</Typography>
                    <Typography variant="caption" display="block" mb={1}>
                      {r.ruleKey} · {r.userType} · {r.activityKey}
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                      <TextField
                        label="Reward coins"
                        size="small"
                        type="number"
                        value={r.rewardCoins ?? 0}
                        onChange={(e) =>
                          updateEarnLocal(r.ruleKey, {
                            rewardCoins: Number(e.target.value),
                          })
                        }
                      />
                      <TextField
                        label="Expiry days"
                        size="small"
                        type="number"
                        value={r.expiryDays ?? ''}
                        onChange={(e) =>
                          updateEarnLocal(r.ruleKey, {
                            expiryDays: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                      />
                      <TextField
                        label="Max daily"
                        size="small"
                        type="number"
                        value={r.maxDaily ?? 0}
                        onChange={(e) =>
                          updateEarnLocal(r.ruleKey, {
                            maxDaily: Number(e.target.value),
                          })
                        }
                      />
                      <TextField
                        select
                        label="Reward type"
                        size="small"
                        value={r.rewardType || 'COINS'}
                        onChange={(e) =>
                          updateEarnLocal(r.ruleKey, {
                            rewardType: e.target.value,
                          })
                        }
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value="COINS">COINS</MenuItem>
                        <MenuItem value="COMMISSION">COMMISSION</MenuItem>
                        <MenuItem value="BOTH">BOTH</MenuItem>
                      </TextField>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!r.isActive}
                            onChange={(e) =>
                              updateEarnLocal(r.ruleKey, {
                                isActive: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Active"
                      />
                      <Button
                        variant="outlined"
                        disabled={saving}
                        onClick={() => saveEarnRule(r)}
                      >
                        Save
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </TabPanel>

            <TabPanel value={tab} index={4}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Every Mitra dashboard card (My Business Desk) and Connect with Loksy
                card is listed here. Coin cost <strong>0</strong> and Active{' '}
                <strong>off</strong> means the feature stays free. Turn Active on
                after you set a price.
                <br />
                <strong>Add Mitra operator</strong> uses included seats (default 4)
                at the base coin cost, then extra seats at the higher extra-seat
                rate. Removing an operator frees a seat; coins already spent are
                not refunded.
              </Alert>
              <Stack spacing={3}>
                {SPEND_GROUPS.map((group) => {
                  const rows = spendRules
                    .filter((r) => spendGroupId(r) === group.id)
                    .sort((a, b) =>
                      String(a.label || '').localeCompare(String(b.label || '')),
                    );
                  if (rows.length === 0) return null;
                  return (
                    <Box key={group.id}>
                      <Typography variant="h6">{group.title}</Typography>
                      <Typography variant="body2" color="text.secondary" mb={1.5}>
                        {group.hint} · {rows.length} features
                      </Typography>
                      <Stack spacing={2}>
                        {rows.map((r) => (
                          <Paper key={r.ruleKey} sx={{ p: 2 }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              mb={0.5}
                            >
                              <Typography fontWeight={600}>{r.label}</Typography>
                              <Chip
                                size="small"
                                label={r.isActive ? 'Priced' : 'Free / off'}
                                color={r.isActive ? 'success' : 'default'}
                              />
                            </Stack>
                            <Typography variant="caption" display="block" mb={1}>
                              {r.ruleKey} · {r.userType} · {r.unitType}
                              {r.sectionKey ? ` · ${r.sectionKey}` : ''}
                            </Typography>
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              spacing={1}
                            >
                              <TextField
                                label="Coin cost"
                                size="small"
                                type="number"
                                value={r.coinCost ?? 0}
                                onChange={(e) =>
                                  updateSpendLocal(r.ruleKey, {
                                    coinCost: Number(e.target.value),
                                  })
                                }
                              />
                              <TextField
                                label="Block units"
                                size="small"
                                type="number"
                                value={r.blockUnits ?? 1}
                                onChange={(e) =>
                                  updateSpendLocal(r.ruleKey, {
                                    blockUnits: Number(e.target.value),
                                  })
                                }
                              />
                              <TextField
                                label="Free limit"
                                size="small"
                                type="number"
                                value={r.freeLimit ?? 0}
                                onChange={(e) =>
                                  updateSpendLocal(r.ruleKey, {
                                    freeLimit: Number(e.target.value),
                                  })
                                }
                              />
                              {r.ruleKey === 'mitra_operator_seat' && (
                                <>
                                  <TextField
                                    label="Included seats"
                                    size="small"
                                    type="number"
                                    value={r.includedSeats ?? r.freeLimit ?? 4}
                                    onChange={(e) =>
                                      updateSpendLocal(r.ruleKey, {
                                        includedSeats: Number(e.target.value),
                                        freeLimit: Number(e.target.value),
                                      })
                                    }
                                    helperText="First N operators"
                                  />
                                  <TextField
                                    label="Extra seat coins"
                                    size="small"
                                    type="number"
                                    value={r.extraCoinCost ?? 0}
                                    onChange={(e) =>
                                      updateSpendLocal(r.ruleKey, {
                                        extraCoinCost: Number(e.target.value),
                                      })
                                    }
                                    helperText="Cost from seat N+1"
                                  />
                                  <TextField
                                    label="Max seats"
                                    size="small"
                                    type="number"
                                    value={r.maxSeats ?? 20}
                                    onChange={(e) =>
                                      updateSpendLocal(r.ruleKey, {
                                        maxSeats: Number(e.target.value),
                                      })
                                    }
                                  />
                                </>
                              )}
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={!!r.isActive}
                                    onChange={(e) =>
                                      updateSpendLocal(r.ruleKey, {
                                        isActive: e.target.checked,
                                      })
                                    }
                                  />
                                }
                                label="Active"
                              />
                              <Button
                                variant="outlined"
                                disabled={saving}
                                onClick={() => saveSpendRule(r)}
                              >
                                Save
                              </Button>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </TabPanel>

            <TabPanel value={tab} index={5}>
              <Paper sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Alert severity="info">
                    Keep coins clearly disclosed as closed-loop Loksy service credits —
                    not cash, not transferable, not withdrawable.
                  </Alert>
                  <TextField
                    label="Terms version"
                    value={settings.coinTermsVersion}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        coinTermsVersion: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="Wallet legal blurb"
                    multiline
                    minRows={2}
                    value={settings.legalBlurb}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, legalBlurb: e.target.value }))
                    }
                  />
                  <TextField
                    label="Refund policy"
                    multiline
                    minRows={2}
                    value={settings.refundPolicyBlurb}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        refundPolicyBlurb: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="Expiry policy"
                    multiline
                    minRows={2}
                    value={settings.expiryPolicyBlurb}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        expiryPolicyBlurb: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="GST / tax display note"
                    multiline
                    minRows={2}
                    value={settings.gstDisplayBlurb}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        gstDisplayBlurb: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="Abuse / reversal note"
                    multiline
                    minRows={2}
                    value={settings.abuseAndReversalBlurb}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        abuseAndReversalBlurb: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="Consent checkbox label"
                    multiline
                    minRows={2}
                    value={settings.consentCheckboxLabel}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        consentCheckboxLabel: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="Full coin terms body"
                    multiline
                    minRows={4}
                    value={settings.coinTermsBody}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        coinTermsBody: e.target.value,
                      }))
                    }
                  />
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                    onClick={saveLegal}
                  >
                    Save legal
                  </Button>
                </Stack>
              </Paper>
            </TabPanel>

            <TabPanel value={tab} index={6}>
              <Paper sx={{ p: 2 }}>
                <Stack spacing={2} maxWidth={480}>
                  <TextField
                    select
                    label="Owner type"
                    value={adjust.ownerType}
                    onChange={(e) =>
                      setAdjust((a) => ({ ...a, ownerType: e.target.value }))
                    }
                  >
                    <MenuItem value="MITRA">MITRA</MenuItem>
                    <MenuItem value="CITIZEN">CITIZEN</MenuItem>
                    <MenuItem value="STUDENT">STUDENT</MenuItem>
                    <MenuItem value="MARKETING_PARTNER">MARKETING_PARTNER</MenuItem>
                  </TextField>
                  <TextField
                    label="Owner ID"
                    value={adjust.ownerId}
                    onChange={(e) =>
                      setAdjust((a) => ({ ...a, ownerId: e.target.value }))
                    }
                  />
                  <TextField
                    select
                    label="Direction"
                    value={adjust.direction}
                    onChange={(e) =>
                      setAdjust((a) => ({ ...a, direction: e.target.value }))
                    }
                  >
                    <MenuItem value="credit">Credit</MenuItem>
                    <MenuItem value="debit">Debit</MenuItem>
                  </TextField>
                  <TextField
                    label="Amount (coins)"
                    type="number"
                    value={adjust.amount}
                    onChange={(e) =>
                      setAdjust((a) => ({ ...a, amount: e.target.value }))
                    }
                  />
                  <TextField
                    label="Expiry days (credits)"
                    type="number"
                    value={adjust.expiryDays}
                    onChange={(e) =>
                      setAdjust((a) => ({ ...a, expiryDays: e.target.value }))
                    }
                  />
                  <TextField
                    label="Description"
                    value={adjust.description}
                    onChange={(e) =>
                      setAdjust((a) => ({ ...a, description: e.target.value }))
                    }
                  />
                  <Divider />
                  <Button
                    variant="contained"
                    disabled={saving}
                    onClick={runAdjust}
                  >
                    Apply adjustment
                  </Button>
                </Stack>
              </Paper>
            </TabPanel>
          </>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default CoinManagement;
